import { computed, type Ref } from 'vue'
import { useTheme } from 'vuetify'

const DARK_THEME_NAME = 'darkTheme'

/**
 * Centralized theme flags derived from Vuetify's useTheme.
 * Defensive: in unit tests or contexts without Vuetify provided, gracefully
 * fall back to a safe default instead of throwing.
 */
export function useThemeFlags(): { isDark: Ref<boolean>; themeName: Ref<string> } {
  // Ensure we always have an object shape compatible with the computed getters.
  let vuetifyTheme: {
    name?: Ref<string>
    current?: Ref<{ dark?: boolean }>
  } = {}

  try {
    // useTheme may throw if Vuetify is not provided (e.g., some unit test contexts)
    vuetifyTheme = useTheme()
  } catch {
    // fallback: keep the empty safe shape so computed() can read without extra guards
    vuetifyTheme = {}
  }

  // Expose the theme name as a string Ref ('' when missing)
  const themeName = computed<string>(() => (vuetifyTheme.name?.value ?? '') as string)

  // Consider the app dark if Vuetify's current.dark is truthy OR we explicitly have the dark theme name.
  const isDark = computed<boolean>(() => {
    const currentDark = !!vuetifyTheme.current?.value?.dark
    const explicitDarkName = themeName.value === DARK_THEME_NAME
    return currentDark || explicitDarkName
  })

  return { isDark, themeName }
}
